/**
 * Minimal, elegant blog slider for "Vieni da Noi".
 * Shows image + title + excerpt. Click opens post in new tab.
 * Responsive: 3 cards/desktop, 2/tablet, 1/mobile.
 * Smooth slide transitions via CSS transforms. Arrow nav + dot indicators.
 * Auto-plays with pause-on-hover.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Newspaper,
  ArrowUpRight,
  CaretLeft,
  CaretRight,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTOPLAY_MS = 6000;

function useVisibleCount() {
  const [count, setCount] = useState(3);
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w < 640) setCount(1);
      else if (w < 1024) setCount(2);
      else setCount(3);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);
  return count;
}

function formatRelative(isoish) {
  if (!isoish) return '';
  const d = new Date(isoish);
  if (isNaN(d)) return '';
  const diffMs = Date.now() - d.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays <= 0) return 'oggi';
  if (diffDays === 1) return 'ieri';
  if (diffDays < 7) return `${diffDays}gg fa`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}sett fa`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} mesi fa`;
  return `${Math.floor(diffDays / 365)}a fa`;
}

export default function BlogSlider() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const visible = useVisibleCount();
  const autoplayRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/news/blog-feed`)
      .then((r) => {
        if (!mounted) return;
        setItems(r.data?.items || []);
      })
      .catch(() => { /* silent */ })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const maxIndex = Math.max(0, items.length - visible);

  // clamp index when visible count changes
  useEffect(() => {
    if (index > maxIndex) setIndex(maxIndex);
  }, [maxIndex, index]);

  // autoplay
  useEffect(() => {
    if (paused || items.length <= visible) return;
    autoplayRef.current = setInterval(() => {
      setIndex((i) => (i >= maxIndex ? 0 : i + 1));
    }, AUTOPLAY_MS);
    return () => clearInterval(autoplayRef.current);
  }, [paused, items.length, visible, maxIndex]);

  const goPrev = () => setIndex((i) => (i <= 0 ? maxIndex : i - 1));
  const goNext = () => setIndex((i) => (i >= maxIndex ? 0 : i + 1));

  if (loading) {
    return (
      <section className="mt-10 sm:mt-14 mb-4" data-testid="blog-slider-section">
        <SectionHeader />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="bg-zinc-100 animate-pulse h-72" />
          ))}
        </div>
      </section>
    );
  }

  if (!items.length) return null;

  const cardWidthPct = 100 / visible;
  const translatePct = index * cardWidthPct;

  return (
    <section
      className="mt-10 sm:mt-14 mb-4"
      data-testid="blog-slider-section"
      aria-label="Novità dal blog Vita da Host"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <SectionHeader onPrev={goPrev} onNext={goNext} canSlide={items.length > visible} />

      {/* Viewport */}
      <div className="relative overflow-hidden">
        <div
          className="flex transition-transform duration-[700ms] ease-[cubic-bezier(.22,.8,.2,1)]"
          style={{ transform: `translateX(-${translatePct}%)` }}
        >
          {items.map((it, i) => (
            <a
              key={it.link + i}
              href={it.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group block flex-shrink-0 px-2 sm:px-2.5 first:pl-0 last:pr-0"
              style={{ width: `${cardWidthPct}%` }}
              data-testid={`blog-card-${i}`}
            >
              <article className="relative bg-white border border-zinc-200 h-full overflow-hidden transition-all duration-500 group-hover:border-zinc-900 group-hover:-translate-y-1 group-hover:shadow-[0_18px_40px_-20px_rgba(0,0,0,0.25)]">
                {/* Image */}
                <div className="relative aspect-[16/10] overflow-hidden bg-zinc-100">
                  {it.image ? (
                    <img
                      src={it.image}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-zinc-100 to-zinc-200 flex items-center justify-center">
                      <Newspaper size={32} weight="thin" className="text-zinc-300" />
                    </div>
                  )}
                  {/* Subtle overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  {/* Date pill */}
                  {it.published && (
                    <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm px-2 py-1 text-[10px] font-black uppercase tracking-wider text-zinc-700">
                      {formatRelative(it.published)}
                    </div>
                  )}
                  {/* External indicator */}
                  <div className="absolute top-3 right-3 w-7 h-7 bg-zinc-900/0 group-hover:bg-zinc-900 flex items-center justify-center transition-all duration-300">
                    <ArrowUpRight
                      size={13}
                      weight="bold"
                      className="text-transparent group-hover:text-white transition-colors duration-300"
                    />
                  </div>
                </div>
                {/* Body */}
                <div className="p-4 sm:p-5">
                  <h4 className="text-sm sm:text-[15px] font-black text-zinc-900 leading-snug tracking-tight line-clamp-2 mb-2 group-hover:text-zinc-700 transition-colors">
                    {it.title}
                  </h4>
                  <p className="text-xs sm:text-[13px] text-zinc-500 leading-relaxed line-clamp-2">
                    {it.excerpt}
                  </p>
                  {/* Read more accent */}
                  <div className="mt-3 flex items-center gap-1 text-[10px] font-black uppercase tracking-[0.22em] text-zinc-400 group-hover:text-zinc-900 transition-colors">
                    Leggi
                    <CaretRight
                      size={10}
                      weight="bold"
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </div>
                </div>
              </article>
            </a>
          ))}
        </div>
      </div>

      {/* Dots (mobile-first, works on all) */}
      {items.length > visible && (
        <div className="flex items-center justify-center gap-1.5 mt-5">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`h-1 transition-all duration-500 ${
                i === index
                  ? 'w-8 bg-zinc-900'
                  : 'w-3 bg-zinc-300 hover:bg-zinc-500'
              }`}
              aria-label={`Vai allo slide ${i + 1}`}
              data-testid={`blog-dot-${i}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function SectionHeader({ onPrev, onNext, canSlide }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-4 sm:mb-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-zinc-900 flex items-center justify-center flex-shrink-0">
          <Newspaper size={18} weight="fill" className="text-white" />
        </div>
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.22em] text-zinc-500">
            Dal blog di Vita da Host
          </p>
          <h3 className="text-base sm:text-xl font-black text-zinc-900 tracking-tight">
            Novità per host
          </h3>
        </div>
      </div>
      {canSlide && (
        <div className="flex items-center gap-1">
          <button
            onClick={onPrev}
            aria-label="Precedente"
            className="w-9 h-9 border border-zinc-200 bg-white hover:border-zinc-900 hover:bg-zinc-900 text-zinc-700 hover:text-white transition-all flex items-center justify-center"
            data-testid="blog-prev-btn"
          >
            <CaretLeft size={14} weight="bold" />
          </button>
          <button
            onClick={onNext}
            aria-label="Successivo"
            className="w-9 h-9 border border-zinc-200 bg-white hover:border-zinc-900 hover:bg-zinc-900 text-zinc-700 hover:text-white transition-all flex items-center justify-center"
            data-testid="blog-next-btn"
          >
            <CaretRight size={14} weight="bold" />
          </button>
        </div>
      )}
    </div>
  );
}
